import React, { useState, useEffect } from "react";
import Pagination from "@/components/pagination";
import Modal from "@/components/modal";
import SearchInput from "@/components/search-input";
import API from "@/services/api";
import toast from "react-hot-toast";
import { BiPlus } from "react-icons/bi";

const Channels = () => {
  const [channels, setChannels] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedData, setSelectedData] = useState(null);
  const limit = 25;

  useEffect(() => {
    fetchChannels();
  }, [currentPage, searchTerm]);

  const fetchChannels = async () => {
    try {
      setLoading(true);
      const response = await API.post("/channel/search", {
        page: currentPage,
        size: limit,
        sort: { created_at: -1 },
        ...(searchTerm && { search: searchTerm }),
      });

      if (!response.ok) throw response;
      setChannels(response.data || []);
      setTotal(response.total || 0);
    } catch (e) {
      console.error("Error fetching channels:", e);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRow = (data) => {
    setSelectedData(data);
  };

  const handleCreateChannel = () => {
    setSelectedData({});
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-gray-800">Channels</h1>
        <div className="flex items-center gap-4">
          <SearchInput
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            className="w-64"
          />
          <CreateChannel fetchChannels={fetchChannels} />
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : channels.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No channels found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3">Index</th>
                  <th className="px-4 py-3">Logo</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Slug</th>
                  <th className="px-4 py-3">Published</th>
                  <th className="px-4 py-3">URL</th>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Created Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {channels.map((channel) => {
                  const channelId = channel._id || channel.id;
                  return (
                    <tr key={channelId} onClick={() => handleViewRow(channel)} className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{channel.index || "-"}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {channel.logo ? (
                          <img src={channel.logo} alt={channel.name} className="h-8 w-8 rounded object-cover" />
                        ) : (
                          <div className="h-8 w-8 rounded bg-gray-200 flex items-center justify-center text-gray-400 text-xs">N/A</div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">{channel.name || "-"}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{channel.slug || "-"}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            channel.published ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {channel.published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {channel.url ? (
                          <a href={channel.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-xs block">
                            {channel.url}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{channel.organization_name || "-"}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{channel.user_name || "-"}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{channel.created_at ? new Date(channel.created_at).toLocaleDateString("fr-FR") : "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="border-t border-gray-200 px-4">
          <Pagination total={total} limit={limit} currentPage={currentPage} onPageChange={setCurrentPage} />
        </div>
      </div>

      <ViewModal isOpen={selectedData !== null} onClose={() => setSelectedData(null)} data={selectedData} fetchData={fetchChannels} />
    </div>
  );
};

const CreateChannel = ({ fetchChannels }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [logo, setLogo] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingLogo(true);
      const rawBody = await readFileAsync(file);
      const { ok, data } = await API.post("/file", { file: { rawBody, name: file.name }, folder: "/channels" });
      if (!ok) return toast.error(e.code);
      setLogo(data);
      toast.success("Logo uploaded successfully");
    } catch (e) {
      toast.error(e.code);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCreateChannel = async (e) => {
    try {
      e.preventDefault();
      const { ok, data } = await API.post("/channel", { name, logo });
      if (!ok) return toast.error(e.code);
      setIsModalOpen(false);
      setName("");
      setLogo("");
      toast.success("Channel created successfully");
      fetchChannels();
    } catch (e) {
      toast.error(e.code);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setIsModalOpen(true)} className="btn btn-primary">
        <BiPlus className="w-4 h-4" />
        <span>Create Channel</span>
      </button>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="w-1/2">
        <div className="p-12">
          <h2 className="text-xl font-semibold mb-4">Create Channel</h2>
          <form onSubmit={handleCreateChannel}>
            <div className="space-y-4">
              <div>
                <label htmlFor="channelName" className="input-label">
                  Name <span className="text-red-500">*</span>
                </label>
                <input type="text" id="channelName" name="name" value={name || ""} onChange={(e) => setName(e.target.value)} className="input" required />
              </div>
              <div>
                <label className="input-label">Logo</label>
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                    disabled={uploadingLogo}
                  />
                  {uploadingLogo && <span className="text-sm text-gray-500">Uploading...</span>}
                </div>
                {logo && (
                  <div className="mt-2">
                    <img src={logo} alt="Logo preview" className="h-16 w-16 rounded object-cover border border-gray-300" />
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button disabled={!name} type="submit" className="btn btn-primary">
                Create Channel
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

const ViewModal = ({ isOpen, onClose, data, fetchData }) => {
  const [activeTab, setActiveTab] = useState("view");
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const isCreateMode = !data || (!data._id && !data.id);

  useEffect(() => {
    if (data) {
      setFormData(data);
    } else {
      setFormData({});
    }
  }, [data]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab("view");
      setFormData({});
    }
  }, [isOpen]);

  const handleFormChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingLogo(true);
      const rawBody = await readFileAsync(file);
      const { ok, data } = await API.post("/file", { file: { rawBody, name: file.name }, folder: "/channels" });
      if (!ok) return toast.error(e.code);
      handleFormChange("logo", data);
      toast.success("Logo uploaded successfully");
    } catch (e) {
      toast.error(e.code);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    if (isCreateMode) {
      // Create new channel
      try {
        setLoading(true);
        const response = await API.post("/channel", formData);

        if (response.ok) {
          toast.success("Channel created");
          fetchData();
          onClose();
        } else {
          toast.error(response.code || "Error creating channel");
        }
      } catch (e) {
        console.error("Error creating channel:", e);
        toast.error(e.code || e.message || "Error creating channel");
      } finally {
        setLoading(false);
      }
    } else {
      // Update existing channel
      const channelId = data._id || data.id;
      if (!channelId) return;

      try {
        setLoading(true);
        const response = await API.put(`/channel/${channelId}`, formData);

        if (response.ok) {
          toast.success("Channel updated");
          fetchData();
          onClose();
        } else {
          toast.error(response.code || "Error updating channel");
        }
      } catch (e) {
        console.error("Error updating channel:", e);
        toast.error(e.code || e.message || "Error updating channel");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async () => {
    if (!data) return;
    const channelId = data._id || data.id;
    if (!channelId) return;

    if (!window.confirm("Are you sure you want to delete this channel?")) {
      return;
    }

    try {
      setLoading(true);
      const response = await API.remove(`/channel/${channelId}`);

      if (response.ok) {
        toast.success("Channel deleted");
        fetchData();
        onClose();
      } else {
        toast.error(response.code || "Error deleting channel");
      }
    } catch (e) {
      console.error("Error deleting channel:", e);
      toast.error(e.code || e.message || "Error deleting channel");
    } finally {
      setLoading(false);
    }
  };

  if (!data) return null;

  const channelId = data?._id || data?.id;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-4xl w-full">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">{isCreateMode ? "Create Channel" : "Channel"}</h2>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab("view")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "view" ? "border-purple-500 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Information
            </button>
            {!isCreateMode && (
              <button
                onClick={() => setActiveTab("raw")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "raw" ? "border-purple-500 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Raw
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "raw" && !isCreateMode ? (
          <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-sm max-h-[70vh]">{JSON.stringify(data, null, 2)}</pre>
        ) : (
          <div className="">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name ?? ""}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                <input
                  type="text"
                  value={formData.slug ?? ""}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>
              <div className="mb-4 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                    disabled={uploadingLogo}
                  />
                  {uploadingLogo && <span className="text-sm text-gray-500">Uploading...</span>}
                </div>
                {formData.logo && (
                  <div className="mt-2">
                    <img src={formData.logo} alt="Logo preview" className="h-16 w-16 rounded object-cover border border-gray-300" />
                  </div>
                )}
              </div>
              <div className="mb-4 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description ?? ""}
                  onChange={(e) => handleFormChange("description", e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="mb-4 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <input
                  type="url"
                  value={formData.url ?? ""}
                  onChange={(e) => handleFormChange("url", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="mb-4 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Private Note</label>
                <textarea
                  value={formData.private_note ?? ""}
                  onChange={(e) => handleFormChange("private_note", e.target.value)}
                  rows={3}
                  placeholder="Internal notes (not visible to users)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
                <p className="mt-1 text-xs text-gray-500">This note is only visible to admins and will not be displayed to users.</p>
              </div>
              <div className="mb-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.published ?? false}
                    onChange={(e) => handleFormChange("published", e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Published</span>
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-between gap-3">
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
              <div className="flex gap-3">
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

// Helper function to read file as base64
function readFileAsync(file) {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default Channels;
